/**
 * The standards library (wave STD rulings 8 to 14): the standards, their editions, each
 * edition's clause tree, the citations the five registers carry, and the edition change.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Reading the library is open to the operator's people; citing a clause, removing a
 * citation and moving citations to a new edition are CONFIGURE_RULES acts (404 by
 * absence to anyone else). An ended contract's plan item takes no new citation and loses
 * none: 409 citation.contract_state, said in the server's words.
 */

import { apiJson, bool, isRecord, num, optStr, str } from "./client";

export interface Standard {
  readonly id: string;
  readonly code: string;
  readonly title: string;
  readonly bodyName: string;
  /** STANDARD or OWNER_SPEC. */
  readonly kind: string;
  /** A product row (shipped with Qualify) rather than the tenant's own. */
  readonly isProduct: boolean;
  readonly currentEditionLabel: string | null;
  readonly editionCount: number;
}

export interface Edition {
  readonly id: string;
  readonly editionLabel: string;
  readonly publishedOn: string | null;
  /** The publication date is known only roughly (a year or a month). */
  readonly publishedOnApproximate: boolean;
  /** CURRENT, SUPERSEDED or WITHDRAWN. */
  readonly status: string;
  readonly successorEditionId: string | null;
  readonly successorEditionLabel: string | null;
  /** When the transition to the successor ends, if the body set one. */
  readonly transitionEndsOn: string | null;
}

export interface Clause {
  readonly id: string;
  /** The clause's own number ("8.5.1"). */
  readonly clauseId: string;
  readonly title: string;
  readonly parentClauseId: string | null;
}

/** One citation as the five registers carry it (CitationOut). */
export interface Citation {
  readonly citationId: string;
  readonly standardCode: string;
  readonly editionLabel: string;
  readonly clauseId: string;
  readonly title: string;
  /** The cited edition is superseded: the citation waits for the edition change. */
  readonly superseded: boolean;
}

/** The registers a clause can be cited from (CitationTargetType). */
export type CitationTargetType =
  "CHECKLIST_BANK_LINE" | "ITP_ITEM" | "VERIFICATION_TYPE" | "DECISION_RULE" | "QC_PROGRAMME_ROW";

export interface CitationTarget {
  readonly type: CitationTargetType;
  readonly id: string;
}

function items(raw: unknown): Record<string, unknown>[] {
  if (!isRecord(raw) || !Array.isArray(raw.items)) {
    throw new Error("Expected an items body");
  }
  return raw.items.filter(isRecord);
}

/** The `citations` of a register row; a row from an older server reads as none. */
export function parseCitations(raw: unknown): Citation[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(isRecord).map((one) => ({
    citationId: str(one.citation_id),
    standardCode: str(one.standard_code),
    editionLabel: str(one.edition_label),
    clauseId: str(one.clause_id),
    title: str(one.title),
    superseded: one.superseded === true,
  }));
}

export async function listStandards(token: string, signal?: AbortSignal): Promise<Standard[]> {
  return apiJson(
    token,
    "/standards",
    (raw) =>
      items(raw).map((one) => ({
        id: str(one.id),
        code: str(one.code),
        title: str(one.title),
        bodyName: str(one.body_name),
        kind: str(one.kind),
        isProduct: bool(one.is_product),
        currentEditionLabel: optStr(one.current_edition_label),
        editionCount: num(one.edition_count),
      })),
    { signal },
  );
}

export async function listEditions(
  token: string,
  standardId: string,
  signal?: AbortSignal,
): Promise<Edition[]> {
  return apiJson(
    token,
    `/standards/${standardId}/editions`,
    (raw) =>
      items(raw).map((one) => ({
        id: str(one.id),
        editionLabel: str(one.edition_label),
        publishedOn: optStr(one.published_on),
        publishedOnApproximate: one.published_on_approximate === true,
        status: str(one.status),
        successorEditionId: optStr(one.successor_edition_id),
        successorEditionLabel: optStr(one.successor_edition_label),
        transitionEndsOn: optStr(one.transition_ends_on),
      })),
    { signal },
  );
}

export async function listClauses(
  token: string,
  standardId: string,
  editionId: string,
  signal?: AbortSignal,
): Promise<Clause[]> {
  return apiJson(
    token,
    `/standards/${standardId}/editions/${editionId}/clauses`,
    (raw) =>
      items(raw).map((one) => ({
        id: str(one.id),
        clauseId: str(one.clause_id),
        title: str(one.title),
        parentClauseId: optStr(one.parent_clause_id),
      })),
    { signal },
  );
}

/** One clause with its depth in the tree, in reading order. */
export interface ClauseNode {
  readonly clause: Clause;
  readonly depth: number;
}

/**
 * The clause tree in reading order. A clause's parent is named by its row id (or, from
 * an older body, its clause number); a clause whose parent is not in the list is a root.
 */
export function clauseTree(clauses: readonly Clause[]): ClauseNode[] {
  const byKey = new Map<string, Clause>();
  for (const one of clauses) {
    byKey.set(one.id, one);
    byKey.set(one.clauseId, one);
  }
  const children = new Map<string, Clause[]>();
  const roots: Clause[] = [];
  for (const one of clauses) {
    const parent = one.parentClauseId === null ? undefined : byKey.get(one.parentClauseId);
    if (parent === undefined || parent.id === one.id) {
      roots.push(one);
    } else {
      children.set(parent.id, [...(children.get(parent.id) ?? []), one]);
    }
  }
  const out: ClauseNode[] = [];
  const seen = new Set<string>();
  const walk = (one: Clause, depth: number): void => {
    if (seen.has(one.id)) {
      return;
    }
    seen.add(one.id);
    out.push({ clause: one, depth });
    for (const child of children.get(one.id) ?? []) {
      walk(child, depth + 1);
    }
  };
  for (const root of roots) {
    walk(root, 0);
  }
  return out;
}

export interface CitationCreated extends Citation {
  readonly targetType: string;
  readonly targetId: string;
}

/** Cite one clause from one record (POST /api/citations). */
export async function cite(
  token: string,
  clauseId: string,
  target: CitationTarget,
): Promise<CitationCreated> {
  return apiJson(
    token,
    "/citations",
    (raw) => {
      const one = isRecord(raw) ? raw : {};
      return {
        citationId: str(one.citation_id),
        standardCode: str(one.standard_code),
        editionLabel: str(one.edition_label),
        clauseId: str(one.clause_id),
        title: str(one.title),
        superseded: one.superseded === true,
        targetType: str(one.target_type),
        targetId: str(one.target_id),
      };
    },
    {
      method: "POST",
      body: { clause_id: clauseId, target_type: target.type, target_id: target.id },
    },
  );
}

/** Remove one citation (DELETE /api/citations/{id}). */
export async function uncite(token: string, citationId: string): Promise<void> {
  await apiJson(token, `/citations/${citationId}`, () => undefined, { method: "DELETE" });
}

/** One row of the edition change's review list (MigrationReviewItem). */
export interface MigrationReview {
  /** SPLIT, MERGED, DROPPED, UNMAPPED, or NEW for a clause new in this edition. */
  readonly relation: string;
  readonly citationId: string | null;
  readonly targetType: string | null;
  readonly targetId: string | null;
  readonly fromClause: string | null;
  readonly toClause: string | null;
}

export interface Migration {
  readonly executed: boolean;
  /** same, folded, split, merged, dropped, unmapped, new: how many of each. */
  readonly counts: Readonly<Record<string, number>>;
  readonly review: readonly MigrationReview[];
}

/**
 * The edition change: re-point the tenant's citations of the editions this one
 * superseded. A dry run (`execute` false) writes nothing and says what would happen.
 */
export async function migrateCitations(
  token: string,
  standardId: string,
  editionId: string,
  execute: boolean,
): Promise<Migration> {
  return apiJson(
    token,
    `/standards/${standardId}/editions/${editionId}/migrate-citations`,
    (raw) => {
      const body = isRecord(raw) ? raw : {};
      const counts = isRecord(body.counts) ? body.counts : {};
      return {
        executed: body.executed === true,
        counts: Object.fromEntries(
          Object.entries(counts).filter(
            (entry): entry is [string, number] => typeof entry[1] === "number",
          ),
        ),
        review: (Array.isArray(body.review) ? body.review : []).filter(isRecord).map((one) => ({
          relation: str(one.relation),
          citationId: optStr(one.citation_id),
          targetType: optStr(one.target_type),
          targetId: optStr(one.target_id),
          fromClause: optStr(one.from_clause),
          toClause: optStr(one.to_clause),
        })),
      };
    },
    { method: "POST", body: { execute } },
  );
}

/* ---------------------------------------------------------------------- */
/* The three configuration registers the library is cited from that have   */
/* no screen of their own yet: decision rules, checklist bank lines and QC  */
/* programme rows. Read only here, for their citations.                     */
/* ---------------------------------------------------------------------- */

export interface CitedRow {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  readonly citations: readonly Citation[];
}

export async function listDecisionRules(token: string, signal?: AbortSignal): Promise<CitedRow[]> {
  return apiJson(
    token,
    "/configuration/decision-rules",
    (raw) =>
      (Array.isArray(raw) ? raw : []).filter(isRecord).map((one) => ({
        id: str(one.id),
        label: `${str(one.object_key)} v${String(num(one.version))}`,
        detail: `${str(one.family)} · ${str(one.status)}`,
        citations: parseCitations(one.citations),
      })),
    { signal },
  );
}

export interface NamedRecord {
  readonly id: string;
  readonly label: string;
}

export async function listChecklistBanks(
  token: string,
  signal?: AbortSignal,
): Promise<NamedRecord[]> {
  return apiJson(
    token,
    "/checklist-banks",
    (raw) =>
      items(raw).map((one) => ({
        id: str(one.id),
        label: `${str(one.code)} ${str(one.title)} (rev ${String(num(one.revision))})`,
      })),
    { signal },
  );
}

export async function listBankLines(
  token: string,
  bankId: string,
  signal?: AbortSignal,
): Promise<CitedRow[]> {
  return apiJson(
    token,
    `/checklist-banks/${bankId}`,
    (raw) => {
      const body = isRecord(raw) ? raw : {};
      return (Array.isArray(body.lines) ? body.lines : []).filter(isRecord).map((one) => ({
        id: str(one.id),
        label: `${str(one.section_number)}.${str(one.line_number)}`,
        detail: str(one.description),
        citations: parseCitations(one.citations),
      }));
    },
    { signal },
  );
}

export async function listProgrammes(token: string, signal?: AbortSignal): Promise<NamedRecord[]> {
  return apiJson(
    token,
    "/configuration/qc-programmes",
    (raw) =>
      (Array.isArray(raw) ? raw : isRecord(raw) && Array.isArray(raw.items) ? raw.items : [])
        .filter(isRecord)
        .map((one) => ({
          id: str(one.id),
          label: `${str(one.title)} (${str(one.equipment_type_code)}, ${str(one.level_code)}, rev ${String(num(one.revision))})`,
        })),
    { signal },
  );
}

export async function listProgrammeRows(
  token: string,
  templateId: string,
  signal?: AbortSignal,
): Promise<CitedRow[]> {
  return apiJson(
    token,
    `/configuration/qc-programmes/${templateId}`,
    (raw) => {
      const body = isRecord(raw) ? raw : {};
      return (Array.isArray(body.rows) ? body.rows : []).filter(isRecord).map((one) => ({
        id: str(one.id),
        label: `${str(one.section)} ${String(num(one.sequence))}`,
        detail: str(one.activity),
        citations: parseCitations(one.citations),
      }));
    },
    { signal },
  );
}
